/**
 * @file panelUtils.ts
 * @description MultipleCascader 面板专用的纯工具函数集合
 *
 * 模块职责：
 * - 路径比较与选中态判断（isSamePath、isPathChecked、isPrefixPath）
 * - 展开链路与列构建（getDefaultExpandedPath、buildColumns）
 * - 搜索过滤与文本处理（getOptionLabelText、normalizeText、filterTreeByKeyword、fixExpandedPathByData）
 * - 叶子路径收集（collectLeafPaths）
 *
 * 约束：
 * - 本模块仅导出纯函数，无副作用，便于单元测试与逻辑复用。
 * - 路径采用 `string[]` 表示，避免 value 含分隔符时产生歧义。
 */

import type { ICascaderMenuOption, TStringPath } from '../types';

/**
 * 判断两条路径是否完全一致。
 *
 * @param a - 路径 A
 * @param b - 路径 B
 * @returns 路径是否逐项相等
 *
 * @remarks
 * 直接逐项比较比 `join('/')` 更稳健，避免 value 本身包含分隔符时产生歧义。
 */
export const isSamePath = (a: TStringPath, b: TStringPath): boolean => {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item === b[index]);
};

/**
 * 判断指定路径是否在已选 value 列表中。
 *
 * @param value - 已选路径数组
 * @param path - 待检查路径
 * @returns 是否已选中
 */
export const isPathChecked = (
  value: TStringPath[],
  path: TStringPath,
): boolean => value.some((item) => isSamePath(item, path));

/**
 * 判断 prefix 是否为 full 的前缀路径。
 *
 * @param prefix - 前缀路径
 * @param full - 完整路径
 * @returns 是否为前缀
 *
 * @remarks
 * 用于取消勾选父节点时，一次性移除其所有后代的已选路径。
 */
export const isPrefixPath = (
  prefix: TStringPath,
  full: TStringPath,
): boolean => {
  if (prefix.length > full.length) {
    return false;
  }
  return prefix.every((item, index) => item === full[index]);
};

/**
 * 默认展开链路：沿每级第一项一路展开到最深层。
 *
 * @param options - 树形选项根节点数组
 * @returns 默认展开的路径（value 数组）
 *
 * @remarks
 * 满足“首次打开即可看到完整链路”的交互要求，降低用户逐级点击成本（需求 8）。
 */
export const getDefaultExpandedPath = (
  options: ICascaderMenuOption[],
): string[] => {
  const path: string[] = [];
  let currentLevel = options;

  while (Array.isArray(currentLevel) && currentLevel.length > 0) {
    const first = currentLevel[0];
    path.push(first.value);
    currentLevel = first.children || [];
  }

  return path;
};

/**
 * 根据展开链路构建“按列展示”的 options。
 *
 * @param options - 树形选项根节点数组（可为搜索过滤后的剪枝树）
 * @param expandedPath - 当前展开路径
 * @returns 每列对应的选项数组，如 [[根级], [第二级], [第三级], ...]
 *
 * @remarks
 * 当某一级的 parent 不存在或无 children 时，后续列应自然截断，避免渲染空列/越界。
 */
export const buildColumns = (
  options: ICascaderMenuOption[],
  expandedPath: string[],
): ICascaderMenuOption[][] => {
  const columns: ICascaderMenuOption[][] = [];
  if (!Array.isArray(options) || options.length === 0) return columns;

  let currentLevel: ICascaderMenuOption[] = options;
  columns.push(currentLevel);

  for (const parentValue of expandedPath) {
    const parent = currentLevel.find((item) => item.value === parentValue);
    if (
      !parent ||
      !Array.isArray(parent.children) ||
      parent.children.length === 0
    ) {
      break;
    }
    currentLevel = parent.children;
    columns.push(currentLevel);
  }

  return columns;
};

/**
 * 从 label 中提取用于搜索/tooltip 的稳定文本。
 *
 * @param label - 节点的 label（ReactNode）
 * @returns 可参与匹配的字符串，非 string/number 返回空串
 *
 * @remarks
 * 仅支持 string/number；对 ReactNode 做文本提取依赖实现细节且不可控。
 */
export const getOptionLabelText = (label: React.ReactNode): string => {
  if (typeof label === 'string') {
    return label;
  }
  if (typeof label === 'number') {
    return String(label);
  }
  return '';
};

/**
 * 文本标准化：去除首尾空格并转为小写，用于搜索匹配。
 *
 * @param value - 原始文本
 * @returns 标准化后的文本
 */
export const normalizeText = (value: string): string =>
  value.trim().toLowerCase();

/**
 * 按关键字过滤树，并保留命中节点的“祖先链路”。
 *
 * @param options - 树形选项根节点数组
 * @param keyword - 搜索关键字
 * @returns 剪枝后的树（保留层级结构）
 *
 * @remarks
 * 级联面板需继续按层级分列展示，必须保留层级结构；用户仍能沿祖先路径展开到命中项（需求 11）。
 */
export const filterTreeByKeyword = (
  options: ICascaderMenuOption[],
  keyword: string,
): ICascaderMenuOption[] => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const normalizedKeyword = normalizeText(keyword);
  if (!normalizedKeyword) {
    return options;
  }

  const dfs = (node: ICascaderMenuOption): ICascaderMenuOption | null => {
    const labelText = normalizeText(getOptionLabelText(node.label));
    const isMatched = labelText.includes(normalizedKeyword);
    if (isMatched) {
      return node;
    }

    const children = Array.isArray(node.children) ? node.children : [];
    if (!children.length) {
      return null;
    }

    const nextChildren = children
      .map((child) => dfs(child))
      .filter(Boolean) as ICascaderMenuOption[];

    if (!nextChildren.length) {
      return null;
    }

    return {
      ...node,
      children: nextChildren,
    };
  };

  return options.map((node) => dfs(node)).filter(Boolean) as ICascaderMenuOption[];
};

/**
 * 当搜索过滤导致原展开项不可见时，修复展开链路以保持“列链路不断裂”。
 *
 * @param options - 过滤后的树形选项
 * @param currentExpandedPath - 当前展开路径
 * @returns 修复后的展开路径
 *
 * @remarks
 * 过滤会剪枝数据树，原 expandedPath[level] 可能在该列消失；若不修复，列会突然中断（需求 11）。
 */
export const fixExpandedPathByData = (
  options: ICascaderMenuOption[],
  currentExpandedPath: string[],
): string[] => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const nextPath: string[] = [];
  let currentLevel = options;
  let level = 0;

  while (Array.isArray(currentLevel) && currentLevel.length > 0) {
    const preferred = currentExpandedPath[level];
    const match = preferred
      ? currentLevel.find((item) => item.value === preferred)
      : undefined;
    const selected = match || currentLevel[0];
    if (!selected) {
      break;
    }
    nextPath.push(selected.value);
    const children = Array.isArray(selected.children) ? selected.children : [];
    if (!children.length) {
      break;
    }
    currentLevel = children;
    level += 1;
  }

  return nextPath;
};

/**
 * 收集指定节点下的所有“叶子路径”。
 *
 * @param root - 根节点（含其 value 在内的完整节点）
 * @param basePath - 到达该节点前的路径（不含 root.value，调用方会拼上）
 * @returns 所有叶子节点的完整路径数组
 *
 * @remarks
 * 用于“勾选父节点 = 勾选其所有叶子”的语义；以“无 children”作为叶子判定，避免依赖后端额外字段（需求 1）。
 */
export const collectLeafPaths = (
  root: ICascaderMenuOption,
  basePath: TStringPath,
): TStringPath[] => {
  const result: TStringPath[] = [];

  const dfs = (node: ICascaderMenuOption, currentPath: TStringPath): void => {
    if (!Array.isArray(node.children) || node.children.length === 0) {
      result.push(currentPath);
      return;
    }
    node.children.forEach((child) => {
      dfs(child, [...currentPath, child.value]);
    });
  };

  dfs(root, basePath);

  return result;
};
